import { type Ref, watch } from 'vue';
import { FloatingTrigger } from '@/components';

interface Config {
  referenceElement: Ref<HTMLElement | undefined>;
  floatingElement: Ref<HTMLElement | undefined>;
  callback: {
    onHover?: (event: MouseEvent) => void;
    onHoverEnd?: (event: MouseEvent) => void;
    onClick?: (event: MouseEvent) => void;
  };
}

export const useTrigger = ({ referenceElement, floatingElement, callback }: Config) => {
  const { onHover, onHoverEnd, onClick } = callback;

  const onClickWrapped = (event: MouseEvent) => {
    event.stopPropagation();
    onClick?.(event);
  };

  const initTrigger = (trigger: `${FloatingTrigger}`) => {
    switch (trigger) {
      case FloatingTrigger.Hover: {
        if (onHover && onHoverEnd) {
          referenceElement.value?.addEventListener('mouseenter', onHover);
          referenceElement.value?.addEventListener('mouseleave', onHoverEnd);

          watch(
            floatingElement,
            (value) => {
              value?.addEventListener('mouseenter', onHover);
              value?.addEventListener('mouseleave', onHoverEnd);
            },
            { immediate: true },
          );
        }
        break;
      }
      case FloatingTrigger.Click: {
        if (onClick) {
          referenceElement.value?.addEventListener('click', onClickWrapped);
        }
        break;
      }
    }
  };

  const destroyTrigger = () => {
    if (onHover) {
      referenceElement.value?.removeEventListener('mouseenter', onHover);
    }
    if (onHoverEnd) {
      referenceElement.value?.removeEventListener('mouseleave', onHoverEnd);
    }
    if (onClick) {
      referenceElement.value?.removeEventListener('click', onClickWrapped);
    }
  };

  return {
    initTrigger,
    destroyTrigger,
  };
};
